import type { ProviderProfileData } from './types';
import styles from './providerProfileCard.module.scss';

export type ProviderProfileCardProps = {
  /** Provider data */
  provider: ProviderProfileData;
  /** Layout variant */
  variant?: 'compact' | 'full';
  /** Custom link component for framework-agnostic routing (e.g., next/link) */
  LinkComponent?: React.ComponentType<{
    href: string;
    className?: string;
    children: React.ReactNode;
  }>;
  /** Additional CSS class */
  className?: string;
};

/**
 * ProviderProfileCard - displays provider info
 * Shows avatar, name, location, rating, and years breeding
 *
 * Note: Use LinkComponent prop to pass your router's Link (e.g., next/link)
 * for client-side navigation. Defaults to a standard anchor tag.
 */
export function ProviderProfileCard({
  provider,
  variant = 'full',
  LinkComponent,
  className
}: ProviderProfileCardProps) {
  const {
    id,
    name,
    slug,
    provinceSlug,
    avatarUrl,
    location,
    rating,
    reviewCount,
    yearsBreeding,
    isVerified
  } = provider;

  const profileUrl = slug
    ? `/breeders/${provinceSlug || 'canada'}/${slug}`
    : `/breeders/${id}`;

  // Use custom Link component if provided, otherwise fall back to <a>
  const Link = LinkComponent || DefaultLink;

  return (
    <div className={`${styles.card} ${styles[variant]} ${className || ''}`}>
      <Link href={profileUrl} className={styles.avatarLink}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            width={variant === 'compact' ? 48 : 80}
            height={variant === 'compact' ? 48 : 80}
            className={styles.avatar}
            loading="lazy"
          />
        ) : (
          <div className={`${styles.avatar} ${styles.avatarPlaceholder}`}>
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </Link>

      <div className={styles.info}>
        <Link href={profileUrl} className={styles.nameLink}>
          <h3 className={styles.name}>
            {name}
            {isVerified && (
              <span className={styles.verifiedBadge} title="Verified Breeder">
                ✓
              </span>
            )}
          </h3>
        </Link>

        {location && <p className={styles.location}>{location}</p>}

        <div className={styles.meta}>
          {rating != null && rating > 0 && (
            <span className={styles.rating}>
              <span className={styles.star}>★</span>
              {rating.toFixed(1)}
              {reviewCount != null && reviewCount > 0 && (
                <span className={styles.reviewCount}>({reviewCount})</span>
              )}
            </span>
          )}

          {yearsBreeding != null && yearsBreeding > 0 && (
            <span className={styles.yearsBreeding}>
              {yearsBreeding} {yearsBreeding === 1 ? 'year' : 'years'} breeding
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Default link component (standard anchor)
function DefaultLink({
  href,
  className,
  children
}: {
  href: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}
